import { Grid } from "@material-ui/core";
import { useRouter } from "@uirouter/react";
import { FunctionComponent, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { ErrorText, useHasCredit } from "../../../components/billing";
import { Form } from "../../../components/forms";
import { EditableField } from "../../../components/forms/field";
import { Select } from "../../../components/forms/select";
import { useAppLoader } from "../../../components/loader/app-loader-hook";
import { Widget } from "../../../components/widget";
import { AppStore } from "../../../store";
import { AuthDTO } from "../../../store/auth/models/dto";
import { ChainDTO } from "../../../store/chain/models/dto";
import { ProductionBlockchains } from "../../../store/chain/models/internal";
import { ChainService } from "../../../store/chain/service";
import { useFetchable } from "../../../store/common/fetchable";
import {
  Fetchable,
  isFetchableSuccess,
} from "../../../store/common/fetchable/helpers";
import { Users } from "../../../store/users/reducer";
import {
  getOption,
  mapObject,
  useYupValidationResolver,
} from "../../../utils/utils";
import { CTA, Section, StyledTextField } from "../../profile/base";
import { BlockchainToolsRoutes } from "../routes";

type RequestDTO = ChainDTO.Requests.PostWalletCheck.T;
const { useFields } = ChainDTO.Requests.PostWalletCheck;

const StyledWidget = styled(Widget)`
  height: auto;
  display: flex;
  flex-direction: column;
`;

export const OnChainAnalysisNew: FunctionComponent<{}> = () => {
  const { labels, validation } = useFields();
  const resolver = useYupValidationResolver(validation);
  const currentUser = useFetchable(Users.current);
  const currentAuth = useSelector<
    AppStore,
    Fetchable<AuthDTO.Responses.Login.T>
  >((store) => store.auth.login);

  const router = useRouter();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<RequestDTO>({
    resolver,
  });

  const loadable = useAppLoader();
  const onSubmit = useCallback(
    (data: RequestDTO) =>
      loadable(
        ChainService.postWalletReport(data).then(() => {
          router.stateService.go(BlockchainToolsRoutes.names.walletReportAll);
        })
      ),
    [loadable, router]
  );

  const hasCredit = useHasCredit("ONCHAIN_WALLET_ANALYSIS");
  const { t } = useTranslation("blockchain-tools");

  const cta = useMemo(
    () =>
      getOption(currentUser).map((curr) => {
        if (!hasCredit) {
          return (
            <>
              <CTA disabled>{t("check-this-wallet")}</CTA>
              <ErrorText>{t("please-top-up-your-account")}</ErrorText>
            </>
          );
        }
        if (
          curr.kycStatus === "VERIFIED" ||
          (isFetchableSuccess(currentAuth) &&
            currentAuth.data.accessToken.roles.includes("ROLE_ADMIN"))
        ) {
          return <CTA>{t("check-this-wallet")}</CTA>;
        }
        return (
          <>
            <CTA disabled>{t("check-this-wallet")}</CTA>
            <ErrorText>
              {t(
                "you-cannot-perform-this-action-because-your-kyc-status-is-not-yet-verified"
              )}
            </ErrorText>
          </>
        );
      }),
    [hasCredit, currentUser]
  );

  return cta.fold(
    () => null,
    (x) => (
      <StyledWidget title={t("on-chain-analysis")}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Section container spacing={3}>
            <Grid item xs={12} md={6}>
              <EditableField editing control={control} name="address">
                <StyledTextField
                  fullWidth
                  type="text"
                  label={labels.address}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                />
              </EditableField>
            </Grid>
            <Grid item xs={12} md={6}>
              <EditableField editing control={control} name="blockchain">
                <Select
                  fullWidth
                  label={labels.blockchain}
                  error={!!errors.blockchain}
                  options={mapObject(
                    ProductionBlockchains.enum,
                    (value, key) => ({
                      title: value,
                      value: key,
                    })
                  )}
                />
              </EditableField>
            </Grid>
          </Section>
          {x}
        </Form>
      </StyledWidget>
    )
  );
};
